import type { ChangeEvent, FormEvent, SyntheticEvent } from 'react'
import type { CatchupPlaybackFromSearch } from '~/entities/catchups/model'
import type { MotnItem } from '~/entities/motn'
import type { SearchResultClickPayload } from '~/features/analytix'
import type {
  MotnResultAnalyticsFields,
  SearchResultAnalyticsArgs,
} from './analytics'

import { MediaTypes } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { debounce, reset } from 'patronum'
import { $movieNightEnabled } from '~/core/flags/options'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { model as catchupsModel } from '~/entities/catchups'
import {
  entitiesHybridSearchModel,
  formatCatchupTimeLabel,
  getSearchCardKind,
  toWatchNowCardFields,
} from '~/entities/hybrid-search'
import { buildMotnLink, entitiesMotnModel } from '~/entities/motn'
import { resolveShowNewLabel } from '~/entities/shows'
import { SEARCH_CONTENT_TYPE, searchResultClicked } from '~/features/analytix'
import { lastWatchedContentFeatureModel } from '~/features/last-watched-content'
import * as pinLockFeature from '~/features/pin-lock'
import { profilesFeaturesModel } from '~/features/profiles'
import { bindSearchAutocompleteRoute } from '~/features/search-autocomplete'
import {
  featuresVoiceSearchModel,
  truncateVoiceSearchResult,
} from '~/features/voice-search'
import { MINIMUM_QUERY_LENGTH_OF_SEARCH_INPUT } from '~/shared/constants/strings'
import { checkIsSearchQueryValid, F, aye, noop, T } from '~/shared/helpers'
import { SEARCH } from '~/shared/routes'
import { resolveNewLabel } from '~/shared/tools'
import { isRequestCancelled } from '~/shared/tools/abort'
import { selectActiveChannel } from '~/widgets/Payment/model/tvModel'
import {
  HYBRID_SEARCH_MAX_LENGTH,
  SEARCH_REQUEST_DELAY,
  TOOLTIP_VISIBLE_DELAY,
} from '../shared/constants'
import { resetSearchOnProfileSwitch } from '../shared/resetOnProfileSwitch'
import {
  EXTERNAL_SECTION,
  toResultClickPayload,
  withMotnResultAnalytics,
  withoutAnalyticsArgs,
  withResultAnalytics,
} from './analytics'

// Events

export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()
export const resetHybridSearchPageState = createEvent()

export const handleChange = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()
export const handleSubmit = createEvent<{
  '<event>': FormEvent<HTMLFormElement>
}>()
export const deleteValue = createEvent()
export const handleHintClick = createEvent<{ hintValue: string }>()

// Debounced events

const searchDebounced = debounce({
  source: handleChange,
  timeout: SEARCH_REQUEST_DELAY,
})
const tooltipDebounced = debounce({
  source: handleChange,
  timeout: TOOLTIP_VISIBLE_DELAY,
})

export const $searchValue = createStore<string>('')
  .on(handleChange, (_, data) => data['<event>'].target.value)
  .reset(deleteValue)

export const $isVisibleTooltip = createStore<boolean>(false).reset(init)
export const $tooltipTitle = combine(i18n.$t, ({ t }) =>
  t('search_min_lengths_hint', {
    count: MINIMUM_QUERY_LENGTH_OF_SEARCH_INPUT,
  })
)

// MOTN: only when hybrid search runs from URL query (not auto/lastWatched path)
sample({
  clock: init,
  source: {
    movieNightEnabled: $movieNightEnabled,
    isActiveKidsProfile: profilesFeaturesModel.$isActiveKidsProfile,
  },
  filter: ({ movieNightEnabled, isActiveKidsProfile }, { query }) =>
    !isActiveKidsProfile &&
    movieNightEnabled &&
    query.has('q') &&
    checkIsSearchQueryValid(query.get('q') || ''),
  fn: (_, { query }) => ({ q: query.get('q') || '' }),
  target: entitiesMotnModel.getMovieNightSearchFx,
})

export const handleMotnClick = createEvent<{
  content: MotnItem & MotnResultAnalyticsFields
}>()

sample({
  clock: handleMotnClick,
  // the whole item is compressed into the link, so the ET-1 coordinates are
  // dropped again before it is built; the section rides along as `routeName` so
  // anything opened from here reports `Search/EXTERNAL` (ET-2.6)
  fn: ({
    content: {
      analyticsPosition: _position,
      analyticsResults: _results,
      analyticsContentType: _contentType,
      ...item
    },
  }) => buildMotnLink(item, { routeName: EXTERNAL_SECTION }),
  target: router.navigatePush,
})

sample({
  clock: handleMotnClick,
  fn: ({ content }): SearchResultClickPayload => ({
    section: EXTERNAL_SECTION,
    position: content.analyticsPosition,
    results: content.analyticsResults,
    contentType: content.analyticsContentType,
    // external content has no id at all, its name is the only identity (ET-1.4)
    externalContentName: content.name,
  }),
  target: searchResultClicked,
})

// FP-4242: ET-1 wrappers. A card may only declare one click handler, so each
// section's click goes through a search-scoped event that reports the selection
// and then hands the untouched payload to the behaviour the card had before.

/** Movies & Series cards, which keep their PIN-lock interception. */
export const handleResultClick = createEvent<
  SearchResultAnalyticsArgs & {
    id?: number
    locked?: boolean
    name?: string
    '<event>': SyntheticEvent
  }
>()

sample({
  clock: handleResultClick,
  fn: toResultClickPayload,
  target: searchResultClicked,
})

sample({
  clock: handleResultClick,
  fn: (args) => withoutAnalyticsArgs(args),
  target: pinLockFeature.intercept,
})

/** Watch Now channel and programme cards, which keep the payment flow. */
export const handleChannelResultClick = createEvent<
  SearchResultAnalyticsArgs & Parameters<typeof selectActiveChannel>[0]
>()

sample({
  clock: handleChannelResultClick,
  fn: toResultClickPayload,
  target: searchResultClicked,
})

sample({
  clock: handleChannelResultClick,
  source: entitiesHybridSearchModel.$hybridSearchSections,
  fn: entitiesHybridSearchModel.buildSearchChannelOrder,
  target: entitiesHybridSearchModel.captureSearchChannelOrder,
})

sample({
  clock: handleChannelResultClick,
  fn: (args) => ({ ...withoutAnalyticsArgs(args), fromSearch: true }),
  target: selectActiveChannel,
})

/**
 * Live Event cards, which navigate through their own link — the router's anchor
 * listener still handles that, so this only reports the click.
 */
export const handleEventResultClick = createEvent<
  SearchResultAnalyticsArgs & { id?: number }
>()

sample({
  clock: handleEventResultClick,
  fn: toResultClickPayload,
  target: searchResultClicked,
})

/**
 * Recently Aired programme cards. The card carries no catch-up or programme id,
 * so both are resolved from the channel and the programme's air time. A locked
 * channel opens the PIN popup here, like Watch Now programme cards, using the
 * channel title rather than the programme name. The section rides along as
 * `routeName` so playback reports `Search/CATCHUPS` (ET-2.6).
 */
export const handleCatchupResultClick = createEvent<
  SearchResultAnalyticsArgs & {
    id?: number
    startMs?: number
    stopMs?: number
    name?: string
    locked?: boolean
  }
>()

type SelectedCatchupFromSearch = CatchupPlaybackFromSearch & {
  locked?: boolean
}

const toCatchupPlaybackParams = ({
  channelId,
  startMs,
  stopMs,
  routeName,
}: SelectedCatchupFromSearch): CatchupPlaybackFromSearch => ({
  channelId,
  startMs,
  stopMs,
  routeName,
})

const $selectedCatchupFromSearch =
  createStore<SelectedCatchupFromSearch | null>(null)
    .on(handleCatchupResultClick, (_, params) =>
      params.id != null && Number.isFinite(params.id)
        ? {
            channelId: params.id,
            startMs: params.startMs,
            stopMs: params.stopMs,
            routeName: params.section,
            locked: params.locked,
          }
        : null
    )
    .reset(left)

sample({
  clock: handleCatchupResultClick,
  fn: toResultClickPayload,
  target: searchResultClicked,
})

sample({
  clock: handleCatchupResultClick,
  source: {
    selectedCatchup: $selectedCatchupFromSearch,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ selectedCatchup, isLocked }) =>
    selectedCatchup != null &&
    isLocked({ id: selectedCatchup.channelId, locked: selectedCatchup.locked }),
  fn: (_, params) => ({
    ...pinLockFeature.emptyPinLockState,
    id: params.id!,
    name: params.name ?? params.contentName ?? '',
    locked: true,
  }),
  target: pinLockFeature.openPinLockPopup,
})

sample({
  clock: handleCatchupResultClick,
  source: {
    selectedCatchup: $selectedCatchupFromSearch,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ selectedCatchup, isLocked }) =>
    selectedCatchup != null &&
    !isLocked({
      id: selectedCatchup.channelId,
      locked: selectedCatchup.locked,
    }),
  fn: ({ selectedCatchup }) => toCatchupPlaybackParams(selectedCatchup!),
  target: catchupsModel.playCatchupFromSearch,
})

sample({
  clock: pinLockFeature.$unlockedId,
  source: {
    selectedCatchup: $selectedCatchupFromSearch,
    unlockChannelId: pinLockFeature.$unlockedId,
  },
  filter: ({ selectedCatchup, unlockChannelId }, value) =>
    aye(value) &&
    selectedCatchup != null &&
    Boolean(selectedCatchup.locked) &&
    selectedCatchup.channelId === unlockChannelId,
  fn: ({ selectedCatchup }) => toCatchupPlaybackParams(selectedCatchup!),
  target: catchupsModel.playCatchupFromSearch,
})

export const $renderEmptyNoVideoViewsPage = createStore(false).reset(left)
export const $renderEmptyNoResultsPage = createStore(false).reset(left)

export const $isOnHybridSearchPage = createStore(false).on(init, T).on(left, F)

// Connections

sample({
  clock: [handleSubmit, searchDebounced],
  source: $searchValue,
  filter: (searchValue) => checkIsSearchQueryValid(searchValue),
  fn: (searchValue) => {
    const query = searchValue.replace(/^\s+|\s+$/gm, '')
    return SEARCH.hybridWithQuery(query, SEARCH_CONTENT_TYPE.manual)
  },
  target: router.navigatePush,
})

// Redirect when query invalid and no lastWatchedContent (case 3)
sample({
  clock: init,
  source: lastWatchedContentFeatureModel.$lastWatchedContent,
  filter: (lastWatchedContent, { query }) => {
    const hasInvalidQuery =
      query.has('q') && !checkIsSearchQueryValid(query.get('q') || '')
    return hasInvalidQuery && !lastWatchedContent?.trim()
  },
  fn: () => SEARCH.ROOT,
  target: router.navigatePush,
})

// Sync $searchValue with the query on every entry (empty when there is no
// valid query). Kept as the current (untrimmed) value when the query already
// matches it: a debounced search re-enters this same route with its own
// trimmed query, and resetting/re-syncing here would drop whitespace the user
// is still typing, cutting the following keystrokes off from it (FP-4248).
sample({
  clock: init,
  source: $searchValue,
  fn: (searchValue, { query }) => {
    const q = query.get('q') || ''
    if (!query.has('q') || !checkIsSearchQueryValid(q)) return ''
    return q === searchValue.replace(/^\s+|\s+$/gm, '') ? searchValue : q
  },
  target: $searchValue,
})

// 1. Valid query -> getHybridSearchFx with query
sample({
  clock: init,
  filter: ({ query }) =>
    query.has('q') && checkIsSearchQueryValid(query.get('q') || ''),
  fn: ({ query }) => ({
    q: query.get('q') || '',
    typeOfSearch: query.get('type') || '',
  }),
  target: entitiesHybridSearchModel.getHybridSearchFx,
})

// 2. Auto search: no valid URL query + lastWatchedContent -> hybrid only, no MOTN
sample({
  clock: init,
  source: lastWatchedContentFeatureModel.$lastWatchedContent,
  filter: (lastWatchedContent, { query }) => {
    const hasValidQuery =
      query.has('q') && checkIsSearchQueryValid(query.get('q') || '')
    return !hasValidQuery && Boolean(lastWatchedContent?.trim())
  },
  fn: (lastWatchedContent) => ({
    q: lastWatchedContent!.trim().toLowerCase(),
    typeOfSearch: SEARCH_CONTENT_TYPE.auto,
  }),
  target: entitiesHybridSearchModel.getHybridSearchFx,
})

// $renderEmptyNoVideoViewsPage when getHybridSearchFx was NOT executed on init
// (invalid query + no lastWatchedContent)
sample({
  clock: init,
  source: lastWatchedContentFeatureModel.$lastWatchedContent,
  fn: (lastWatchedContent, { query }) => {
    const hasValidQuery =
      query.has('q') && checkIsSearchQueryValid(query.get('q') || '')
    const hasLastWatched = Boolean(lastWatchedContent?.trim())
    return !hasValidQuery && !hasLastWatched
  },
  target: $renderEmptyNoVideoViewsPage,
})

sample({
  clock: init,
  source: {
    searchHints: entitiesHybridSearchModel.$searchHints,
    isActiveKidsProfile: profilesFeaturesModel.$isActiveKidsProfile,
  },
  filter: ({ searchHints, isActiveKidsProfile }) =>
    !isActiveKidsProfile && !searchHints?.length,
  fn: noop,
  target: entitiesHybridSearchModel.getSearchHintsFx,
})

// $renderEmptyNoResultsPage: true when getHybridSearchFx returned empty or failed
sample({
  clock: entitiesHybridSearchModel.getHybridSearchFx.done,
  source: entitiesHybridSearchModel.$lastQuery,
  filter: (lastQuery, { params }) => params.q === lastQuery,
  fn: (_, { result }) => result.length === 0,
  target: $renderEmptyNoResultsPage,
})

sample({
  clock: entitiesHybridSearchModel.getHybridSearchFx.done,
  source: entitiesHybridSearchModel.$lastQuery,
  filter: (lastQuery, { params, result }) =>
    params.q === lastQuery && result.length > 0,
  fn: F,
  target: $renderEmptyNoResultsPage,
})

sample({
  clock: entitiesHybridSearchModel.getHybridSearchFx.fail,
  source: entitiesHybridSearchModel.$lastQuery,
  filter: (lastQuery, { params, error }) =>
    params.q === lastQuery && !isRequestCancelled(error),
  fn: T,
  target: $renderEmptyNoResultsPage,
})

reset({
  clock: [init, left],
  target: [
    entitiesHybridSearchModel.$hybridSearchResults,
    entitiesMotnModel.$motnSearchResults,
    $renderEmptyNoResultsPage,
  ],
})

reset({
  clock: left,
  target: entitiesHybridSearchModel.$searchHints,
})

reset({
  clock: resetHybridSearchPageState,
  target: [
    $searchValue,
    $isVisibleTooltip,
    $renderEmptyNoVideoViewsPage,
    $renderEmptyNoResultsPage,
    $isOnHybridSearchPage,
  ],
})

sample({
  clock: resetSearchOnProfileSwitch,
  target: resetHybridSearchPageState,
})

sample({
  clock: [handleSubmit, tooltipDebounced],
  source: $searchValue,
  fn: (value) => !checkIsSearchQueryValid(value),
  target: $isVisibleTooltip,
})

sample({
  clock: $searchValue,
  filter: (value: string) => checkIsSearchQueryValid(value),
  fn: F,
  target: $isVisibleTooltip,
})

// Voice search result: navigate with voice source (max 300 chars at word boundary)
sample({
  clock: featuresVoiceSearchModel.applyVoiceSearchResult,
  source: $isOnHybridSearchPage,
  filter: (isOnPage, result) => {
    const q = truncateVoiceSearchResult(result)
    return isOnPage && checkIsSearchQueryValid(q)
  },
  fn: (_, result) =>
    SEARCH.hybridWithQuery(
      truncateVoiceSearchResult(result),
      SEARCH_CONTENT_TYPE.voice
    ),
  target: router.navigatePush,
})

bindSearchAutocompleteRoute({
  $searchValue,
  $isActive: $isOnHybridSearchPage,
  typingClock: handleChange,
  resetAutocompleteOn: [left],
  getNavigateToOnPick: (query) =>
    SEARCH.hybridWithQuery(query, SEARCH_CONTENT_TYPE.manual),
  voiceSearchResultClock: featuresVoiceSearchModel.applyVoiceSearchResult,
})

// handleHintClick: search with hint value if different from current search
sample({
  clock: handleHintClick,
  source: $searchValue,
  filter: (searchValue, payload) => {
    const trimmed = payload.hintValue.trim()
    return checkIsSearchQueryValid(trimmed) && trimmed !== searchValue.trim()
  },
  fn: (_searchValue, payload) => {
    const query = payload.hintValue.trim()
    return SEARCH.hybridWithQuery(query, SEARCH_CONTENT_TYPE.manual)
  },
  target: router.navigatePush,
})

// FP-4236: with MOTN rendering independently of hybrid, do not flash the
// "only external results" title while the internal search is still pending.
export function getMotnGridTitleKey({
  hybridPending,
  hybridHasResults,
}: {
  hybridPending: boolean
  hybridHasResults: boolean
}):
  | 'search_external_results_title'
  | 'search_only_external_results_found_title' {
  return hybridPending || hybridHasResults
    ? 'search_external_results_title'
    : 'search_only_external_results_found_title'
}

// FP-4240: Watch Now progress and remaining time are read off the clock when
// the rendered results change, so the whole result set shares one consistent
// "now" and a superseded request cannot re-time the visible one
const $nowMs = createStore(Date.now()).on(
  entitiesHybridSearchModel.$hybridSearchResults,
  () => Date.now()
)

// FP-4238: section headers are resolved here so the skin can render a grid per
// section straight from the store (CR-409 LK-1…LK-4). Catch-up items also get
// the card discriminator and their date-time label, both of which depend on the
// active locale, so they recompute with the titles (FP-4241, UI-5.8). A Watch
// Now channel on air overrides that kind with its own card (FP-4240, UI-5.6).
export const $hybridSearchSections = combine(
  i18n.$t,
  entitiesHybridSearchModel.$hybridSearchSections,
  $nowMs,
  ({ t }, sections, nowMs) =>
    sections.map((section) => ({
      ...section,
      title: t(section.titleKey),
      // FP-4242: each card carries its own ET-1 coordinates, so a click reports
      // the section, the rank inside it and how many results it offered without
      // the handler knowing anything about the grid (ET-1.6, ET-1.14, ET-1.15)
      items: withResultAnalytics(
        section.items.map((item) => {
          const cardKind = getSearchCardKind(item)
          const watchNowFields = toWatchNowCardFields(item, nowMs, t)

          // TV Show priority-resolved server-side; movie and channel badges
          // share resolveNewLabel. Watch Now / Recently Aired stay TV_CHANNEL
          // items, so they keep the short badge after the programme-card
          // kind override.
          const newLabel =
            item.contentType === MediaTypes.TV_SHOW && 'labels' in item
              ? resolveShowNewLabel(item, t)
              : (item.contentType === MediaTypes.VOD ||
                    item.contentType === MediaTypes.TV_CHANNEL) &&
                  'labels' in item
                ? resolveNewLabel(item, t)
                : undefined

          return {
            ...item,
            cardKind,
            catchupTimeLabel: formatCatchupTimeLabel(item.programme),
            newLabel,
            ...watchNowFields,
          }
        }),
        section.key
      ),
    }))
)

// FP-4242: External results live in their own model and their own grid, so they
// get the same ET-1 coordinates here instead of through the section grid
const $motnSearchResultsWithAnalytics = combine(
  entitiesMotnModel.$motnSearchResults,
  (items) => (items == null ? null : withMotnResultAnalytics(items))
)

const $motnGridTitle = combine(
  i18n.$t,
  entitiesHybridSearchModel.$hybridSearchResults,
  entitiesHybridSearchModel.$hybridPending,
  ({ t }, hybridSearchResults, hybridPending) =>
    t(
      getMotnGridTitleKey({
        hybridPending,
        hybridHasResults: Boolean(
          hybridSearchResults && hybridSearchResults.length
        ),
      })
    )
)

register($searchValue, '$search/hybrid/searchValue')
register($isVisibleTooltip, '$search/hybrid/isVisibleTooltip')
register($tooltipTitle, '$search/hybrid/tooltipTitle')
register(handleChange, 'search/hybrid/handleChange')
register(handleSubmit, 'search/hybrid/handleSubmit')
register(deleteValue, 'search/hybrid/deleteValue')
register(entitiesHybridSearchModel.$searchHints, '$search/hybrid/searchHints')
register(
  entitiesHybridSearchModel.$hybridSearchResults,
  '$search/hybrid/hybridSearchResults'
)
register($hybridSearchSections, '$search/hybrid/hybridSearchSections')
// identity of the current result set: collapses every expanded section grid
// back to 2 rows when a new search takes over (UI-7.6.1)
register(
  entitiesHybridSearchModel.$lastQuery,
  '$search/hybrid/hybridSearchQuery'
)
register(
  $renderEmptyNoVideoViewsPage,
  '$search/hybrid/renderEmptyNoVideoViewsPage'
)
register($renderEmptyNoResultsPage, '$search/hybrid/renderEmptyNoResultsPage')
register($motnSearchResultsWithAnalytics, '$search/hybrid/motnSearchResults')
register($motnGridTitle, '$search/hybrid/motnGridTitle')
register(handleMotnClick, 'search/hybrid/handleMotnClick')
register(handleResultClick, 'search/hybrid/handleResultClick')
register(handleChannelResultClick, 'search/hybrid/handleChannelResultClick')
register(handleCatchupResultClick, 'search/hybrid/handleCatchupResultClick')
register(handleEventResultClick, 'search/hybrid/handleEventResultClick')
register(handleHintClick, 'search/hybrid/handleHintClick')
register(
  createStore(HYBRID_SEARCH_MAX_LENGTH),
  '$search/hybrid/hybridSearchMaxLength'
)
